import { ref } from 'vue'
import ENV from '@/engine/env/web.env'
import svgCluster from '@/assets/inline/common/map_pin_cluster.svg?raw'
import svg from '@/assets/inline/common/map_pin.svg?raw'

export function useLoadGoogleMaps ({
  withInitialGeolocation = false,
  center = { lat: 40.4637, lng: 3.7492 },
  zoom = 5
} = {}) {
  interface Data {
    googleUrl: string
    queries: string
    googleScript?: HTMLScriptElement
    el?: HTMLElement
  }
  const data: Data = {
    googleUrl: 'https://maps.googleapis.com/maps/api/js',
    queries: `?key=${ENV.google.maps}&libraries=places&v=weekly&solution_channel=GMP_QB_addressselection_v1_cABC`
  }

  const map = ref<google.maps.Map>()

  const methods = {
    createMap (resolve: () => void) {
      if (!data.el) { resolve(); return }

      map.value = new window.google.maps.Map(data.el, {
        center: { lat: center.lat ?? 40.4637, lng: center.lng ?? 3.7492 },
        zoom,
        minZoom: 5,
        maxZoom: 18
      })

      if (withInitialGeolocation && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          ({ coords: { latitude: lat, longitude: lng } }) => {
            map.value?.setCenter?.({ lat, lng })
            map.value?.setZoom(11)
          }
        )
      }

      resolve()
    },

    async initMap ({ el } = { el: HTMLElement.prototype }) {
      await new Promise<void>((resolve) => {
        data.el ??= el

        data.googleScript = document.getElementById('gmaps-script') as HTMLScriptElement

        if (data.googleScript) {
          methods.createMap(resolve)
          return
        }

        data.googleScript = document.createElement('script')
        data.googleScript.setAttribute('src', `${data.googleUrl}${data.queries}`)
        data.googleScript.setAttribute('id', 'gmaps-script')
        document.head.appendChild(data.googleScript)

        data.googleScript.onload = () => {
          methods.createMap(resolve)
        }
      })
    },

    createCustomMarkerIcon () {
      const svgBtoa = window.btoa(svg)

      return {
        url: `data:image/svg+xml;base64,${svgBtoa}`,
        scaledSize: new window.google.maps.Size(45, 45),
        labelOrigin: new google.maps.Point(20, 40)
      }
    },

    createCustomCluster () {
      return {
        render ({ count, position }: { count: number, position: any }) {
          const svgBtoa = window.btoa(svgCluster)

          // create marker using svg icon
          return new window.google.maps.Marker({
            position,
            icon: {
              url: `data:image/svg+xml;base64,${svgBtoa}`,
              scaledSize: new window.google.maps.Size(45, 45),
              labelOrigin: new google.maps.Point(18, 15)
            },
            label: {
              text: String(count),
              color: 'rgba(255,255,255)',
              fontSize: '12px'
            },
            // adjust zIndex to be above other markers
            zIndex: 1000 + count
          })
        }
      }
    },

    async geocode (address: google.maps.GeocoderRequest) {
      const geocoder = new window.google.maps.Geocoder()
      const result = await geocoder.geocode(address)

      return result
    }
  }

  return {
    map,
    initMap: methods.initMap,
    createCustomCluster: methods.createCustomCluster,
    createCustomMarkerIcon: methods.createCustomMarkerIcon,
    geocode: methods.geocode
  }
}
